import { Link } from '@apollo/orbit';
import { GraphQLObjectType } from 'graphql';
import { TypeMap } from 'graphql/type/schema';
import qs from 'query-string';
import React from 'react';
import { NavLink } from 'react-router-dom';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { safeCompileMarkdown } from 'src/lib/safeCompileMarkdown';

import { getSubjectSlugByGraphQLType } from '../getSubjectSlugByGraphQLType';

const possibleFieldLinkRegex = /([^\[\w`]|^)([A-Z]\w*)\.(\w+)([^\]\w`]|$)/g;

const schemaReferencePath = 'schema';

export const DescriptionMarkdownRenderer = ({
  descriptionMarkdown,
  typeMap,
  additionalNavigationEffects,
}: {
  descriptionMarkdown: string;
  typeMap: TypeMap;
  additionalNavigationEffects?: () => void;
}) => {
  const { locationToReference } = useInternalGraphLinking();

  return safeCompileMarkdown(
    descriptionMarkdown.replace(
      possibleFieldLinkRegex,
      (match, prefix, typeName, fieldName, suffix) => {
        const type = typeMap[typeName];
        if (
          type instanceof GraphQLObjectType &&
          fieldName in type.getFields()
        ) {
          return `${prefix}[${typeName}.${fieldName}](${schemaReferencePath}?typeName=${typeName}&fieldName=${fieldName})${suffix}`;
        }
        return match;
      },
    ),
    {
      overrides: {
        // This is meant to replace the anchors added above
        a: {
          component: (props) => {
            // If manual <a> tag was misformed we might not have an href
            if (typeof props.href !== 'string') {
              return <Link isExternal {...props} to={props.href} />;
            }
            const { url, query } = qs.parseUrl(props.href);

            if (url !== schemaReferencePath) {
              return <Link isExternal {...props} to={props.href} />;
            }
            const typeName = query.typeName;
            const fieldName = query.fieldName;
            if (
              !typeName ||
              !fieldName ||
              typeof typeName !== 'string' ||
              typeof fieldName !== 'string'
            ) {
              throw new Error(
                'schema links must have [data-type-name] and [data-field-name]',
              );
            }
            const type = typeMap[typeName];
            if (!type) {
              throw new Error(`Could not find type '${typeName}' in schema`);
            }
            return (
              <NavLink
                to={locationToReference({
                  category: getSubjectSlugByGraphQLType(type),
                  graphqlTypeName: typeName,
                  linkedGraphqlEntity: fieldName,
                })}
                onClick={additionalNavigationEffects}
              >
                {props.children}
              </NavLink>
            );
          },
        },
        ul: {
          props: {
            // revert the css reset that tailwind added
            // this allows nested bullets to use differing symbols as set by browser defaults
            className: `list-[revert] m-[revert] p-[revert]`,
          },
        },
      },
    },
  );
};
